import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { ChevronLeft, RefreshCw, Package, Truck } from 'lucide-react';
import DeliveryDetailModal from './controltower/DeliveryDetailModal';
import PickupDetailModal from './controltower/PickupDetailModal';
import SequenceModal from './controltower/SequenceModal';
import DeliveriesList from './controltower/DeliveriesList';
import PickupsList from './controltower/PickupsList';

const API_URL = process.env.REACT_APP_API_URL;

function ControlTower({ userId, setActiveComponent }) {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  
  const [deliveryPoints, setDeliveryPoints] = useState([]);
  const [pickupOrders, setPickupOrders] = useState([]);
  const [deliveryMarkers, setDeliveryMarkers] = useState([]);
  const [pickupMarkers, setPickupMarkers] = useState([]);
  
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [selectedPickup, setSelectedPickup] = useState(null);
  const [sequencePoint, setSequencePoint] = useState(null);
  const [showDeliveriesList, setShowDeliveriesList] = useState(false);
  const [showPickupsList, setShowPickupsList] = useState(false);
  
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Fetch data from API
  async function fetchData() {
    try {
      const [deliveriesRes, pickupsRes] = await Promise.all([
        axios.get(`${API_URL}/api/delivery_points/driver/${userId}`),
        axios.get(`${API_URL}/api/delivery_orders/driver/${userId}`)
      ]);

      setDeliveryPoints(deliveriesRes.data);
      setPickupOrders(pickupsRes.data);
      setLastUpdate(new Date());
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setIsLoading(false);
    }
  }

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current) return;

    // Create map centered on Lima, Peru
    mapRef.current = L.map(mapContainerRef.current).setView([-12.0464, -77.0428], 13);

    // Add tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(mapRef.current);

    // Initial data fetch
    fetchData();

    // Set up polling every 5 seconds
    const interval = setInterval(fetchData, 5000);

    return () => {
      clearInterval(interval);
      if (mapRef.current) {
        mapRef.current.remove();
      }
    };
  }, [userId]);

  // Check if there are active pickups (pending or encamino)
  function hasActivePic() {
    return pickupOrders.some(
      pickup => pickup.status === 'pending' || pickup.status === 'encamino'
    );
  }

  // Update pickup markers
  useEffect(() => {
    if (!mapRef.current || pickupOrders.length === 0) return;

    // Remove existing pickup markers
    pickupMarkers.forEach(marker => marker.remove());

    const newMarkers = pickupOrders
      .filter(pickup => pickup.latitude && pickup.longitude)
      .map(pickup => {
        let color = 'blue';

        // Color based on status
        switch (pickup.status) {
          case 'pending':
            color = '#FF6B35'; // Orange for pending pickups
            break;
          case 'encamino':
            color = '#4A90E2'; // Blue for in progress
            break;
          case 'recogido':
            color = '#67A43C'; // Green for picked up
            break;
          case 'cancelado':
            color = '#E74C3C'; // Red for cancelled
            break;
          default:
            color = '#95A5A6'; // Gray for others
        }

        // Create custom icon for pickups (square shape)
        const icon = L.divIcon({
          html: `<div style="
            background-color: ${color};
            width: 28px;
            height: 28px;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 11px;
            color: white;
            font-weight: bold;
          ">📦</div>`,
          className: '',
          iconSize: [28, 28],
          iconAnchor: [14, 14],
        });

        const marker = L.marker([pickup.latitude, pickup.longitude], {
          icon,
          title: `Recojo - ${pickup.customer_name || 'N/A'}`,
        })
          .on('click', () => {
            if (pickup.status === 'pending') {
              setSequencePoint({ ...pickup, type: 'pickup' });
            } else {
              setSelectedPickup(pickup);
            }
          })
          .addTo(mapRef.current);

        return marker;
      });

    setPickupMarkers(newMarkers);
  }, [pickupOrders]);

  // Update delivery markers
  useEffect(() => {
    if (!mapRef.current || deliveryPoints.length === 0) return;

    // Remove existing delivery markers
    deliveryMarkers.forEach(marker => marker.remove());

    // Don't render delivery markers if there are active pickups
    if (hasActivePic()) {
      setDeliveryMarkers([]);
      return;
    }

    const newMarkers = deliveryPoints
    .filter(point => point.latitude && point.longitude)
    .map(point => {
      let color = 'blue';

      switch (point.status) {
        case 'pending':
        case 'postergado':
          switch (point.slot) {
            case 1:
              color = '#C62828'; // deep red — earliest
              break;
            case 2:
              color = '#E53935'; // red
              break;
            case 3:
              color = '#F57C00'; // orange
              break;
            case 4:
              color = '#FBC02D'; // yellow — use dark text on marker
              break;
            case 5:
              color = '#AFB42B'; // yellow-green
              break;
            case 6:
              color = '#43A047'; // green
              break;
            case 7:
              color = '#00ACC1'; // teal
              break;
            case 8:
              color = '#1E88E5'; // blue
              break;
            case 9:
              color = '#5E35B1'; // purple — latest
              break;
            default:
              color = '#95A5A6'; // gray fallback
          }
          break;
        case 'encamino':
          color = '#4A90E2';
          break;
        case 'entregado':
          color = '#67A43C';
          break;
        case 'cancelado':
          color = '#E74C3C';
          break;
        case 'rechazado':
          color = '#C0392B';
          break;
        default:
          color = '#95A5A6';
      }

      const icon = L.divIcon({
        html: `<div style="
          background-color: ${color};
          width: 24px;
          height: 24px;
          border: 2px solid white;
          border-radius: 50%;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          color: white;
          font-weight: bold;
        ">${point.sequence || ''}</div>`,
        className: '',
        iconSize: [24, 24],
        iconAnchor: [12, 12],
      });

      const marker = L.marker([point.latitude, point.longitude], {
        icon,
        title: `Entrega - ${point.recipient_name || 'N/A'}`,
      })
        .on('click', () => {
          if (['pending', 'postergado'].includes(point.status)) {
            setSequencePoint({ ...point, type: 'delivery' });
          } else if (['entregado', 'encamino', 'rechazado', 'cancelado'].includes(point.status)) {
            setSelectedDelivery(point);
          }
        })
        .addTo(mapRef.current);

      return marker;
    });

    setDeliveryMarkers(newMarkers);
  }, [deliveryPoints, pickupOrders]);

  // Handle sequence update
  async function handleSequenceUpdate(sequence) {
    if (!sequencePoint) return;

    try {
      const endpoint = sequencePoint.type === 'pickup' 
        ? '/api/change/order_sequence'
        : '/api/change/sequence';

      await axios.post(`${API_URL}${endpoint}`, {
        id: sequencePoint.id,
        sequence: parseInt(sequence, 10)
      });

      // Refresh data
      await fetchData();
      setSequencePoint(null);
    } catch (error) {
      console.error('Error updating sequence:', error);
      alert('Error al actualizar la secuencia');
    }
  }

  // Handle delivery item click from list
  function handleDeliveryClick(delivery) {
    setShowDeliveriesList(false);
    if (delivery.status === 'pending') {
      setSequencePoint({ ...delivery, type: 'delivery' });
    } else if (['entregado', 'encamino', 'rechazado', 'cancelado'].includes(delivery.status)) {
      setSelectedDelivery(delivery);
    }
  }

  // Handle pickup item click from list
  function handlePickupClick(pickup) {
    setShowPickupsList(false);
    if (pickup.status === 'pending') {
      setSequencePoint({ ...pickup, type: 'pickup' });
    } else {
      setSelectedPickup(pickup);
    }
  }

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setActiveComponent('OptionsDriver')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-slate-700" />
              </button>
              <div>
                <h1 className="text-lg sm:text-xl font-extrabold text-[#B1336E] font-montserrat">
                  Torre de Control
                </h1>
                <p className="text-xs text-slate-600 font-montserrat">
                  Actualizado: {lastUpdate.toLocaleTimeString('es-PE')}
                </p>
              </div>
            </div>
            
            <button
              onClick={fetchData}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              disabled={isLoading}
            >
              <RefreshCw className={`w-5 h-5 text-slate-700 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Bar - Now Clickable */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-4 py-2">
        <div className="max-w-7xl mx-auto flex gap-4 justify-center">
          <button
            onClick={() => setShowDeliveriesList(true)}
            className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-[#B1336E]/10 to-[#E6B43C]/10 rounded-lg hover:from-[#B1336E]/20 hover:to-[#E6B43C]/20 transition-all active:scale-95"
          >
            <Truck className="w-4 h-4 text-[#B1336E]" />
            <span className="text-xs font-montserrat font-semibold text-slate-700">
              Entregas: <span className="text-[#B1336E]">{deliveryPoints.length}</span>
            </span>
          </button>
          <button
            onClick={() => setShowPickupsList(true)}
            className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-[#67A43C]/10 to-[#E6B43C]/10 rounded-lg hover:from-[#67A43C]/20 hover:to-[#E6B43C]/20 transition-all active:scale-95"
          >
            <Package className="w-4 h-4 text-[#67A43C]" />
            <span className="text-xs font-montserrat font-semibold text-slate-700">
              Recojos: <span className="text-[#67A43C]">{pickupOrders.length}</span>
            </span>
          </button>
        </div>
      </div>

      {/* Map Container */}
      <div className="flex-1 relative">
        <div ref={mapContainerRef} className="absolute inset-0" />
        
        {/* Active Pickups Warning */}
        {hasActivePic() && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] max-w-sm">
            <div className="bg-gradient-to-r from-[#FF6B35] to-[#E65100] text-white px-4 py-2 rounded-lg shadow-lg">
              <p className="text-sm font-montserrat font-semibold text-center">
                📦 Recojos pendientes - Las entregas se mostrarán después
              </p>
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="absolute bottom-4 left-4 z-[1000] bg-white rounded-lg shadow-lg p-3 max-w-xs">
          <h3 className="text-xs font-bold text-slate-900 font-montserrat mb-2">
            Leyenda
          </h3>
          <div className="space-y-1.5 text-xs font-montserrat">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 bg-[#BF360C] rounded-full border-2 border-white shadow"></div>
              <span>Urgente (Slot 1-2)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 bg-[#F57C00] rounded-full border-2 border-white shadow"></div>
              <span>Normal (Slot 3)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 bg-[#FFB74D] rounded-full border-2 border-white shadow"></div>
              <span>Flexible (Slot 4-5)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 bg-[#4A90E2] rounded-full border-2 border-white shadow"></div>
              <span>En camino</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 bg-[#67A43C] rounded-full border-2 border-white shadow"></div>
              <span>Entregado</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 bg-[#FF6B35] border-2 border-white shadow"></div>
              <span>📦 Recojo</span>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {selectedDelivery && (
        <DeliveryDetailModal
          delivery={selectedDelivery}
          onClose={() => setSelectedDelivery(null)}
        />
      )}

      {selectedPickup && (
        <PickupDetailModal
          pickup={selectedPickup}
          onClose={() => setSelectedPickup(null)}
        />
      )}

      {sequencePoint && (
        <SequenceModal
          point={sequencePoint}
          onClose={() => setSequencePoint(null)}
          onSubmit={handleSequenceUpdate}
        />
      )}

      {/* Deliveries List */}
      {showDeliveriesList && (
        <DeliveriesList
          deliveries={deliveryPoints}
          onClose={() => setShowDeliveriesList(false)}
          onDeliveryClick={handleDeliveryClick}
        />
      )}

      {/* Pickups List */}
      {showPickupsList && (
        <PickupsList
          pickups={pickupOrders}
          onClose={() => setShowPickupsList(false)}
          onPickupClick={handlePickupClick}
        />
      )}
    </div>
  );
}

export default ControlTower;